import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorText,
  Input,
  Label,
  PageTitle,
} from '@dataroom/shared';
import {
  formatDay,
  formatMoney,
  type PayoutReport,
  type PayoutReportRow,
  type PlatformAffiliate,
} from '@/lib/affiliates';

/**
 * The platform owner's affiliate console (ADR-0042): every affiliate with its
 * funnel and ledger totals, and the payout report. NOTHING here pays anyone:
 * the owner pays each affiliate outside ClosingVault (monthly ACH direct
 * deposit), then records it with "Mark paid" and the reference of that payment. The API
 * answers 404 to anyone who is not the platform owner.
 */

const REPORT_KEY = ['platform', 'payout-report'];
const AFFILIATES_KEY = ['platform', 'affiliates'];

export function PlatformAffiliatesPage() {
  const affiliates = useQuery({
    queryKey: AFFILIATES_KEY,
    queryFn: () =>
      api
        .get<{ affiliates: PlatformAffiliate[] }>('/platform/affiliates')
        .then((r) => r.affiliates),
    retry: false,
  });
  const report = useQuery({
    queryKey: REPORT_KEY,
    queryFn: () => api.get<PayoutReport>('/platform/affiliate-payouts/report'),
    retry: false,
  });

  if (affiliates.error instanceof ApiError && affiliates.error.status === 401) {
    return <Navigate to="/login" replace />;
  }
  if (affiliates.error instanceof ApiError && affiliates.error.status === 404) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mx-auto w-full max-w-container space-y-6 px-4 py-6 md:px-6">
      <PageTitle>Affiliates</PageTitle>
      <PayoutReportCard report={report.data} error={report.error} loading={report.isPending} />
      <Card>
        <CardHeader>
          <CardTitle>All affiliates</CardTitle>
        </CardHeader>
        <CardContent>
          {affiliates.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : affiliates.error ? (
            <ErrorText error={affiliates.error} />
          ) : affiliates.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">No one has joined yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3 font-medium">Affiliate</th>
                    <th className="py-2 pr-3 font-medium">Code</th>
                    <th className="py-2 pr-3 font-medium">Clicks</th>
                    <th className="py-2 pr-3 font-medium">Signups</th>
                    <th className="py-2 pr-3 font-medium">Paying</th>
                    <th className="py-2 pr-3 font-medium">On hold</th>
                    <th className="py-2 pr-3 font-medium">Payable</th>
                    <th className="py-2 pr-3 font-medium">Paid</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {affiliates.data.map((a) => (
                    <AffiliateRow key={a.id} affiliate={a} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function moneyList(
  totals: PlatformAffiliate['totals'],
  pick: (t: PlatformAffiliate['totals'][number]) => number,
): string {
  if (totals.length === 0) return '—';
  return totals.map((t) => formatMoney(pick(t), t.currency)).join(' · ');
}

function AffiliateRow({ affiliate: a }: { affiliate: PlatformAffiliate }) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () =>
      api.post(`/platform/affiliates/${a.id}/status`, {
        status: a.status === 'active' ? 'suspended' : 'active',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: AFFILIATES_KEY }),
  });
  return (
    <tr className="border-t align-top">
      <td className="py-2 pr-3">
        <div className="font-medium">{a.name}</div>
        <div className="text-muted-foreground">{a.email}</div>
        <div className="text-xs text-muted-foreground">Joined {formatDay(a.createdAt)}</div>
      </td>
      <td className="py-2 pr-3 font-mono">{a.referralCode}</td>
      <td className="py-2 pr-3">{a.funnel.clicks}</td>
      <td className="py-2 pr-3">{a.funnel.signups}</td>
      <td className="py-2 pr-3">{a.funnel.payingFirms}</td>
      <td className="py-2 pr-3">{moneyList(a.totals, (t) => t.onHoldCents)}</td>
      <td className="py-2 pr-3">
        {moneyList(a.totals, (t) => t.payableCents)}
        {a.status === 'suspended' && a.totals.some((t) => t.payableCents > 0) ? (
          <div className="text-xs font-medium text-destructive">held — suspended</div>
        ) : null}
      </td>
      <td className="py-2 pr-3">
        {moneyList(a.totals, (t) => t.paidCents)}
        {a.totals.some((t) => t.excludedCents > 0) ? (
          <div className="text-xs text-muted-foreground">
            Excluded (self-referral):{' '}
            {a.totals
              .filter((t) => t.excludedCents > 0)
              .map((t) => formatMoney(t.excludedCents, t.currency))
              .join(' · ')}
          </div>
        ) : null}
      </td>
      <td className="py-2">
        <div>{a.status === 'active' ? 'Active' : 'Suspended'}</div>
        <Button
          variant="ghost"
          className="h-auto px-0 py-1 text-xs"
          disabled={toggle.isPending}
          onClick={() => toggle.mutate()}
        >
          {a.status === 'active' ? 'Suspend' : 'Reinstate'}
        </Button>
        <ErrorText error={toggle.error} className="text-xs" />
      </td>
    </tr>
  );
}

function PayoutReportCard({
  report,
  error,
  loading,
}: {
  report: PayoutReport | undefined;
  error: unknown;
  loading: boolean;
}) {
  const qc = useQueryClient();
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Payout report</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void qc.invalidateQueries({ queryKey: REPORT_KEY })}
            >
              Refresh
            </Button>
            {report ? (
              <a
                className="inline-flex h-10 items-center rounded-md border px-4 text-sm font-medium hover:bg-muted"
                href={`/api/v1/platform/affiliate-payouts/report.csv?asOf=${encodeURIComponent(report.asOf)}`}
                download
              >
                Download CSV
              </a>
            ) : null}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Commissions past their 60-day hold, net of refunds and chargebacks, for affiliates at or
          above the $100 minimum, plus early payout requests (a fee of $5 or 2% is kept: send the
          net). Smaller balances roll forward. Pay each affiliate outside ClosingVault, then record
          it here with the payment reference and a fresh code from your authenticator. Suspended
          affiliates are held and not listed.
          {report ? ` As of ${new Date(report.asOf).toLocaleString()}.` : ''}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error || !report ? (
          <ErrorText error={error} />
        ) : report.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing is payable right now.</p>
        ) : (
          <ul className="divide-y">
            {report.rows.map((row) => (
              <PayoutRow key={`${row.affiliateId}:${row.currency}`} row={row} asOf={report.asOf} />
            ))}
          </ul>
        )}
        {report && report.recentPayouts.length > 0 ? (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Recorded payouts</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              {report.recentPayouts.map((p) => (
                <li key={p.id}>
                  {formatDay(p.markedPaidAt)} — {p.affiliateName}:{' '}
                  {formatMoney(p.totalCents, p.currency)}
                  {p.feeCents > 0
                    ? ` less a ${formatMoney(p.feeCents, p.currency)} fee = ${formatMoney(p.netCents, p.currency)}`
                    : ''}{' '}
                  ({p.reference})
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function PayoutRow({ row, asOf }: { row: PayoutReportRow; asOf: string }) {
  const qc = useQueryClient();
  const [reference, setReference] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const markPaid = useMutation({
    mutationFn: () =>
      api.post('/platform/affiliate-payouts', {
        affiliateId: row.affiliateId,
        currency: row.currency,
        asOf,
        expectedTotalCents: row.totalCents,
        expectedNetCents: row.netCents,
        reference: reference.trim(),
        totpCode: totpCode.trim(),
      }),
    onSettled: () => setTotpCode(''),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: REPORT_KEY });
      await qc.invalidateQueries({ queryKey: AFFILIATES_KEY });
    },
  });
  const inputId = `ref-${row.affiliateId}-${row.currency}`;
  return (
    <li className="flex flex-col gap-3 py-3 md:flex-row md:items-end md:justify-between">
      <div className="space-y-0.5 text-sm">
        <div className="font-medium">
          {row.name} — {formatMoney(row.totalCents, row.currency)}
        </div>
        {row.early ? (
          <div className="text-xs font-medium">
            Early payout requested: {formatMoney(row.totalCents, row.currency)} gross, a{' '}
            {formatMoney(row.feeCents, row.currency)} fee. Send{' '}
            {formatMoney(row.netCents, row.currency)}.
          </div>
        ) : null}
        <div className="text-xs text-muted-foreground">
          {row.entryCount} {row.entryCount === 1 ? 'entry' : 'entries'} · {row.email}
        </div>
      </div>
      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          markPaid.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor={inputId}>Payment reference</Label>
          <Input
            id={inputId}
            maxLength={200}
            placeholder="e.g. ACH trace or batch reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${inputId}-totp`}>Authenticator code</Label>
          <Input
            id={`${inputId}-totp`}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            className="w-28"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
            required
          />
        </div>
        <Button
          type="submit"
          disabled={markPaid.isPending || !reference.trim() || totpCode.length !== 6}
        >
          {markPaid.isPending ? 'Recording…' : 'Mark paid'}
        </Button>
        <ErrorText error={markPaid.error} />
      </form>
    </li>
  );
}
